import type { ReactElement } from "react";
import { Link } from "react-router";

import type { FieldErrors } from "../../api/client.js";
import { FormSummary } from "../../forms/fields.js";
import { useSearchForm } from "../../forms/use-search-form.js";

import { DateField, errorOf, summaryOf, text, type PaymentConditions, type PaymentResultChoice } from "./payment-search-form.js";
import { adminCenterUrls } from "./urls.js";

/**
 * PAY.JP の課金を探す窓。<b>課金の結果の窓（<c>payment-search-form.tsx</c>）と同じ決まり</b>——
 * Enter で探してもフォーカスとスクロールを保つ・探し直すと 1 ページ目・契約 ID は欄に入れて保つ・期間の断りは欄の下、ほかは先頭。
 * 結果の選択肢（成功・失敗・返金）はサーバが返す。
 */
export function ChargeSearchForm({ conditions, results, errors }: {
    readonly conditions: PaymentConditions;
    readonly results: readonly PaymentResultChoice[];
    readonly errors: FieldErrors;
}): ReactElement {
    const form = useSearchForm(adminCenterUrls.charges(), {
        from: text(conditions.from), to: text(conditions.to), result: text(conditions.result), keyword: text(conditions.keyword),
        tenantId: text(conditions.tenantId),
    });

    return (
        <section className="card mb-4 p-6">
            <form method="get" {...form.props}>
                <FormSummary messages={summaryOf(errors)} />
                <div className="mb-3">
                    <label htmlFor="keyword" className="form-label">契約・PAY.JP の ID で探す</label>
                    <input
                        id="keyword" name="keyword" className="form-input" autoComplete="off"
                        defaultValue={text(conditions.keyword)} placeholder="システム名・企業名・顧客 ID・課金 ID の一部"
                    />
                </div>

                <div className="flex flex-wrap items-end gap-3">
                    <DateField name="from" label="この日から" value={text(conditions.from)} error={errorOf(errors, "from")} />
                    <DateField name="to" label="この日まで" value={text(conditions.to)} error={errorOf(errors, "to")} />
                    <div>
                        <label htmlFor="result" className="form-label">結果</label>
                        <select id="result" name="result" className="form-input" defaultValue={text(conditions.result)}>
                            <option value="">すべて</option>
                            {results.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="tenantId" className="form-label">契約 ID</label>
                        <input
                            id="tenantId" name="tenantId" type="number" className="form-input" autoComplete="off"
                            defaultValue={text(conditions.tenantId)}
                        />
                    </div>
                    {/* 送るボタンとクリアはひとまとまりにする（折り返すときも一緒に次の行へ落ちる。片方だけ別の行に分かれない） */}
                    <div className="flex gap-3">
                        <button type="submit" className="btn-primary">検索</button>
                        {/* **クリアは「条件の無い URL を開く」。**type="reset" は描いたときの値に戻るだけ */}
                        <Link to={adminCenterUrls.charges()} className="btn-secondary">クリア</Link>
                    </div>
                </div>
                <p className="form-help">
                    <strong className="text-ink-800">期間の既定は今日までの 1 か月</strong>（長くて 1 年）です。新しい課金から順に並べます。
                </p>
            </form>
        </section>
    );
}
